<template>

    <Panel>

        <PanelHeader heading="Setting up your new sale" subheading="Duplicate an existing sale or start from scratch" />

        <PanelBody>
            
            <Fieldset>
                <FormField :field="state.sale_form.fields.auctioneer_id" namespace="sale_" :name="'auctioneer_id'" class="mb-4" />
                <FormField :field="state.sale_form.fields.duplicate_existing_sale" namespace="sale_" :name="'duplicate_existing_sale'" class="mb-4" />
                <FormField :field="state.sale_form.fields.duplicate_sale_id" namespace="sale_" :name="'duplicate_sale_id'" class="mb-4" />
                <FormField :field="state.sale_form.fields.sale_type_id" namespace="sale_" :name="'sale_type_id'" class="mb-4" />
                <FormField :field="state.sale_form.fields.format" namespace="sale_" :name="'format'" class="mb-4" />
                <FormField :field="state.sale_form.fields.name" namespace="sale_" :name="'name'" class="mb-4" />
                <FormField :field="state.sale_form.fields.description" namespace="sale_" :name="'description'" class="mb-4" />
            </Fieldset>


        </PanelBody>

        <PanelFooter>
            <Button type="button" width="w-auto" color="text-blue" class="ml-2" :onClick="() => ROUTER.push({ name: 'sales.index' })">Cancel</Button>
            <Button type="button" width="w-auto" class="ml-2" :onClick="() => next_step()" :disabled="!next_button_active">Next</Button>
        </PanelFooter>

    </Panel>

</template>

<script>
import { useSaleStore } from '@sto/sale.store'
import { useSystemStore } from '@sto/system.store'
import { useAuthStore } from '@sto/auth.store'
import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import { isValid, getPayload } from '@lib/helpers'
import { useRoute, useRouter } from 'vue-router'
import CONFIG from '@lib/config'
import moment from 'moment'
import LoadingIcon from '@ico/LoadingIcon.vue'
export default {
    components: {
        LoadingIcon
    },
    setup() {
        const ROUTE = useRoute()
        const ROUTER = useRouter()
        const SALE_STORE = useSaleStore()
        const SYSTEM_STORE = useSystemStore()
        const AUTH_STORE = useAuthStore()

        const user_auctioneer_options = computed(() => {
            return AUTH_STORE.user_auctioneers.map(({id, organisation_name}) => {
                return {
                    'value': id,
                    'label': organisation_name
                }
            })
        })

        const state = reactive({
            sales: [],
            template_sale: null,
            template_sale_type: null,
            sale_form: {
                show_fields: false,
                valid: false,
                has_changes: computed(() => {
                    let has_changes = false
                    for (let f in state.sale_form.fields) {
                        if (state.sale_form.fields[f].value != state.sale_form.fields[f].stored_value) has_changes = true
                    }
                    return has_changes
                }),
                loading: false,
                fields: {
                    duplicate_existing_sale: {
                        active: true,
                        label: 'Duplicate existing sale?',
                        type: 'select',
                        value: false,
                        stored_value: '',
                        options: [
                            { value: 1, label: 'Yes, copy settings from existing sale' },
                            { value: 0, label: 'No, start from scratch' }
                        ],
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    duplicate_sale_id: {
                        active: false,
                        label: 'Sale to duplicate',
                        type: 'select',
                        value: '',
                        stored_value: '',
                        options: [],
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    auctioneer_id: {
                        active: true,
                        label: 'Auctioneer',
                        type: 'select',
                        value: Array.isArray(user_auctioneer_options.value) && user_auctioneer_options.value.length > 0 ? user_auctioneer_options.value[0].value : null,
                        options: Array.isArray(user_auctioneer_options.value) ? user_auctioneer_options.value : [],
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    sale_type_id: {
                        active: false,
                        label: 'Sale type',
                        type: 'select',
                        value: '',
                        stored_value: '',
                        options: [],
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    name: {
                        active: false,
                        label: 'Sale name',
                        type: 'text',
                        value: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    format: {
                        active: false,
                        label: 'Format',
                        type: 'select',
                        value: 'online',
                        options: [
                            { value: 'online', label: 'Online' },
                            { value: 'physical', label: 'Physical' },
                            { value: 'hybrid', label: 'Hybrid' },
                        ],
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    description: {
                        active: true,
                        label: 'Description',
                        type: 'richtext',
                        value: '',
                        stored_value: '',
                        richtext_options: {
                            formatting: ['bold', 'italic', 'ul']
                        },
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                }
            },
        })

        /**
         * should the next button be available to the user?
         */
         const next_button_active = computed(() => {
            let active = true
            for (let f in state.sale_form.fields) {
                if (state.sale_form.fields[f].required && !state.sale_form.fields[f].value) active = false
            }
            return active
        })

        const getAuctioneerSaleTypes = async () => {

            state.sale_form.fields.sale_type_id.active = false
            state.sale_form.fields.sale_type_id.options = []
            state.sale_form.fields.sale_type_id.value = ''

            // stop if no auctioneer id
            if (!state.sale_form.fields.auctioneer_id.value) return
            // get the sale types
            const sale_types = await SALE_STORE.getAuctioneerSaleTypes(state.sale_form.fields.auctioneer_id.value)

            if (!sale_types.length) return

            let sale_type_options = sale_types.map(({name, id}) => { return { value: id, label: name }})
            sale_type_options.push({ value: null, label: 'Choose sale type...'})
            state.sale_form.fields.sale_type_id.options = sale_type_options
            state.sale_form.fields.sale_type_id.value = null
            state.sale_form.fields.sale_type_id.active = true
        }

        const setSaleValues = async () => {
            // handle the sale type id
             if (state.sale_form.fields.duplicate_existing_sale.value == 1 && state.template_sale) {
                 state.sale_form.fields.sale_type_id.disabled = true
                 state.sale_form.fields.sale_type_id.active = true
                 state.sale_form.fields.sale_type_id.value = state.template_sale.sale_type_id
                 // make sure we have this sale type data in state
                 if (!state.template_sale_type || state.template_sale_type?.id != state.sale_form.fields.sale_type_id.value) {
                     state.template_sale_type = await getSaleType(state.sale_form.fields.sale_type_id.value)
                 }
             }

            if (state.sale_form.fields.duplicate_existing_sale.value == 1 && state.template_sale?.name) {
                state.sale_form.fields.name.value = state.template_sale.name

            } else if (state.template_sale_type?.name) {
                state.sale_form.fields.name.value = state.template_sale_type.name
            }
            state.sale_form.fields.name.active = true

            if (state.sale_form.fields.duplicate_existing_sale.value == 1 && state.template_sale?.description) {
                state.sale_form.fields.description.value = state.template_sale.description

            } else if (state.template_sale_type?.default_public_description) {
                state.sale_form.fields.description.value = state.template_sale_type.default_public_description
            }
            state.sale_form.fields.description.active = true

            if (state.sale_form.fields.format.value == 1 && state.template_sale?.format) {
                state.sale_form.fields.format.value = state.template_sale.format

            } else if (state.template_sale_type?.default_sale_format) {
                state.sale_form.fields.format.value = state.template_sale_type.default_sale_format
            }
            state.sale_form.fields.format.active = true

        }

        /**
         * handles the fetching of a sale type's data and setting it into state
         * @returns {Promise<void>}
         */
        const setSaleTypeData = async () => {
            // make sure we have a sale type id to work with
            if (!state.sale_form.fields.sale_type_id.value) return console.warn('setSaleTypeData: tried to set sale type data with null sale type id')
            // don't proceed if this field has been set to disabled (means that the sale type is being returned from a duplicated sale
            if (state.sale_form.fields.sale_type_id.disabled) return

            try {
                // fetch the sale type data
                const sale_type = await getSaleType(state.sale_form.fields.sale_type_id.value)
                state.template_sale_type = sale_type
                    ? sale_type
                    : null

            } catch (error) {
                console.error('getSaleType error', error)

            } finally {
                setSaleValues()
            }
        }

        /**
         * fetches a sale type by id
         * @returns {Promise<void>}
         */
        const getSaleType = async sale_type_id => {
            return SALE_STORE.getSaleTypeById(sale_type_id)
        }

        /**
         * handles the fetching of auctioneer sales and setting the UI up accordingly
         * @param async
         */
        const setAuctioneerSalesData = async () => {
            // reset existing sales
            state.sales = []
            const sales = await getAuctioneerSales(state.sale_form.fields.auctioneer_id.value)
            state.sales = Array.isArray(sales?.sales)
                ? sales.sales
                : []
            let sale_options = state.sales.map(({name, id, starts_at_date}) => { return { value: id, label: `${name} (${moment(starts_at_date).format(CONFIG.dates.formats.brief_date)})` }})
            sale_options.push({ value: null, label: 'Choose sale...'})
            state.sale_form.fields.duplicate_sale_id.options = sale_options
            state.sale_form.fields.duplicate_sale_id.value = null
            state.sale_form.fields.duplicate_sale_id.active = true
        }

        /**
         * fetches the auctioneer sales
         * @param async
         */
        const getAuctioneerSales = async auctioneer_id => {
            return SALE_STORE.getForUser(
                AUTH_STORE.user?.id,
                [
                    { key: 'auctioneer_id', value: auctioneer_id },
                    { key: 'per_page', value: 100 },
                ]
            )
        }

        const next_step = async () => {

            // stop if loading
            if (state.sale_form.loading) return
            // check validity - jump ship if not
            if (!isValid(state.sale_form.fields)) {
                return state.sale_form.loading = false
            }

            try {
                const payload = getPayload(state.sale_form.fields)
                const draft_sale = await SALE_STORE.createDraftSale(payload)
                ROUTER.push({ name: 'sales.new.configure', params: { sale_id: draft_sale.id } })
            } catch (error) {
                console.error('error', error)
                SYSTEM_STORE.addError('Something went wrong', error?.message || 'Unable to create this sale')
            }
        }

        watch (
            () => state.sale_form.fields.sale_type_id.value,
            (new_value) => {
                setSaleTypeData()
            }
        )

        watch(
            () => state.sale_form.fields.auctioneer_id.value,
            (new_value, old_value) => {
                getAuctioneerSaleTypes()
            }
        )

        watch(
            () => state.sale_form.fields.duplicate_existing_sale.value,
            (new_value, old_value) => {
                if (new_value == 1) {
                    setAuctioneerSalesData()
                } else {
                    state.sales = []
                    state.template_sale = null
                    state.template_sale_type = null
                    state.sale_form.fields.duplicate_sale_id.active = false
                    state.sale_form.fields.sale_type_id.disabled = false
                    state.sale_form.fields.sale_type_id.value = null
                    state.sale_form.fields.name.value = ''
                    state.sale_form.fields.description.value = ''
                    state.sale_form.fields.format.value = ''
                }
            }
        )

        watch(
            () => state.sale_form.fields.duplicate_sale_id.value,
            (new_value, old_value) => {
                if (new_value) {
                    const sale = state.sales.find(s => s.id == new_value)
                    state.template_sale = sale
                        ? sale
                        : null

                    SALE_STORE.setDuplicateSaleTemplate(state.template_sale)

                    setSaleValues()
                }
            }
        )

        watch(
            () => user_auctioneer_options.value,
            (new_value, old_value) => {
                // if it turns out we only have one auctioneer, use that by default and hide the dropdown
                if (Array.isArray(user_auctioneer_options.value) && user_auctioneer_options.value.length == 1) {
                    state.sale_form.fields.auctioneer_id.value = user_auctioneer_options.value[0].value
                    state.sale_form.fields.auctioneer_id.type = 'hidden'
                }
            }
        )

        onBeforeMount(() => {
            getAuctioneerSaleTypes()
        })

        return {
            SALE_STORE,
            ROUTER,
            next_button_active,
            state,
            next_step
        }
    }
}

</script>

